import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ParseResult } from 'papaparse';
import { useCSVReader } from 'react-papaparse';
import { CsvRow } from './AvaSuburbListUploadCsvModal';
import { AU_STATES, VALID_TIMEZONES } from '@constants';

type Props = {
  addInvalidHeader: (header: string) => void;
  addValidationError: (error: string) => void;
  clearState: () => void;
  setCsvRows: (rows: CsvRow[]) => void;
};

enum HeaderName {
  Key = 'key',
  SuburbName = 'suburb_name',
  TimeZone = 'timezone',
  SuburbState = 'suburb_state',
  ThirdPartyLogistics = 'third_party_logistics',
  SuburbPostcode = 'suburb_postcode',
}

const AvaSuburuListCsvReader: FC<Props> = ({ addInvalidHeader, addValidationError, clearState, setCsvRows }) => {
  const validHeaders = Object.values(HeaderName);

  const { CSVReader } = useCSVReader();

  const validateHeaders = (headers: string[]) => {
    headers.forEach((header) => {
      const headerIsValid = validHeaders.includes(header as HeaderName);

      if (!headerIsValid) {
        addInvalidHeader(header);
      }
    });
  };

  const convertCsvToRows = (csv: ParseResult<string[]>) => {
    const headers: string[] = csv.data[0];

    validateHeaders(headers);
    const rows = csv.data.slice(1);
    const seenKeys = new Set();

    const csvRows: CsvRow[] = rows.map((row) => {
      return headers.reduce((csvRow: CsvRow, header: string, i: number) => {
        const newRow = {
          ...csvRow,
          [header]: row[i],
        };

        if (header === HeaderName.TimeZone && !VALID_TIMEZONES.some((timezone) => timezone.value === newRow[header])) {
          addValidationError(`Value for ${header} must be a valid timezone in ${newRow[HeaderName.Key]}`);
        }

        if (header === HeaderName.SuburbState && !AU_STATES.some((state) => state.id === newRow[header])) {
          addValidationError(`Value for ${header} must be a valid state in ${newRow[HeaderName.Key]}.`);
        }

        if (header === HeaderName.SuburbPostcode) {
          const valueAsNumber = Number(newRow[header]);

          isNaN(valueAsNumber) && addValidationError(`Value for ${header} must be a number in ${newRow[HeaderName.Key]}.`);
        }

        // Check for duplicate key
        if (header === HeaderName.Key) {
          if (seenKeys.has(newRow[header])) {
            addValidationError(`Duplicate suburb key found: ${newRow[HeaderName.Key]}`);
          } else {
            seenKeys.add(newRow[header]);
          }
        }

        return newRow;
      }, {});
    });

    setCsvRows(csvRows);
  };

  const handleOnFileLoad = (data: ParseResult<string[]>) => {
    clearState();
    convertCsvToRows(data);
  };

  return (
    <CSVReader onUploadAccepted={(results: any) => handleOnFileLoad(results)}>
      {({ getRootProps, acceptedFile }: any) => (
        <Box minWidth={400}>
          <Typography variant="subtitle1" mb={1} data-testid="ava-suburb-list-reader-file-name-text">
            {(acceptedFile && acceptedFile?.name) || 'No file selected'}
          </Typography>
          <Button variant="outlined" component="span" {...getRootProps()} data-testid="ava-suburb-list-reader-choose-file-btn">
            {acceptedFile ? 'Choose another file' : 'Choose file'}
          </Button>
        </Box>
      )}
    </CSVReader>
  );
};

export default AvaSuburuListCsvReader;
